/**
 * PCM16 in and out for the live conversation.
 *
 * WebRTC gave all of this for free — capture, resampling, jitter buffer,
 * playback. Since the customer's network blocks the UDP that WebRTC needs, audio
 * travels over Socket.IO instead and this file is the part that has to be done by
 * hand. It stays deliberately small; everything that decides *what* is spoken
 * lives on the server.
 */

import { createMicrophoneInput } from "@/lib/microphone-input";

/** Both directions of the realtime API: mono PCM16 at 24 kHz. */
export const PCM_RATE = 24_000;

export function floatToPcm16Base64(samples: Float32Array): string {
  const pcm = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    pcm[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  const bytes = new Uint8Array(pcm.buffer);
  let binary = "";
  // In chunks: a spread over 100 ms of audio is fine, but the argument list of
  // String.fromCharCode has a limit that longer buffers would hit.
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  return btoa(binary);
}

// The explicit ArrayBuffer parameter matters: `copyToChannel` refuses a
// Float32Array over an ArrayBufferLike.
function base64ToFloat(encoded: string): Float32Array<ArrayBuffer> {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const pcm = new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2));
  const samples = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) samples[i] = pcm[i] / 0x8000;
  return samples;
}

export type Recorder = {
  stop: () => void;
  switchMicrophone: (deviceId: string | null) => Promise<void>;
  readonly microphoneLabel: string;
  /** The microphone, for the level meter in the call overlay. */
  stream: MediaStream;
};

/** Joins the ~100 ms blocks of the worklet before they are encoded once. */
export function concatSamples(blocks: Float32Array[]): Float32Array {
  const total = blocks.reduce((sum, block) => sum + block.length, 0);
  const joined = new Float32Array(total);
  let offset = 0;
  for (const block of blocks) {
    joined.set(block, offset);
    offset += block.length;
  }
  return joined;
}

/**
 * Starts capturing and calls `onChunk` roughly every 100 ms with raw samples.
 *
 * Raw and not base64 on purpose: over long polling every frame is an HTTP
 * request, so the caller coalesces several blocks before encoding — and
 * concatenating base64 strings would not produce valid audio.
 *
 * The context is created at 24 kHz so the browser resamples the microphone for
 * us; doing it by hand is the classic source of a chipmunk voice.
 */
export async function startRecorder(
  onChunk: (samples: Float32Array) => void,
): Promise<Recorder> {
  const input = await createMicrophoneInput(
    {
      // Without a peer connection there is no WebRTC echo canceller, so these are
      // asked for explicitly. They still apply to the captured track, but they are
      // weaker than the full loop — which is why the UI recommends headphones.
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    { sampleRate: PCM_RATE },
  );
  const { context, stream } = input;
  try {
    // Loaded by URL from `public/`, so the name is a runtime contract, not an
    // import the compiler checks.
    await context.audioWorklet.addModule("/pcm-recorder-worklet.js");

    const source = context.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(context, "pcm-recorder");
    node.port.onmessage = (event) => {
      onChunk(event.data as Float32Array);
    };
    source.connect(node);
    // Not connected to the destination on purpose: that would play the advisor's
    // own microphone back at them.

    return {
      stream,
      switchMicrophone: input.switchMicrophone,
      get microphoneLabel() {
        return input.label;
      },
      stop: () => {
        node.port.onmessage = null;
        node.disconnect();
        source.disconnect();
        input.stop();
      },
    };
  } catch (error) {
    input.stop();
    throw error;
  }
}

/**
 * Plays the answer as it arrives.
 *
 * Deltas are scheduled back to back on a write pointer instead of played on
 * arrival: playing each chunk immediately produces a click between every 100 ms
 * of audio.
 */
export class PcmPlayer {
  private context: AudioContext | null = null;
  private writeAt = 0;
  private sources = new Set<AudioBufferSourceNode>();
  private tap: MediaStreamAudioDestinationNode | null = null;
  private draining = false;

  /**
   * How far ahead of the playhead the first chunk is scheduled.
   *
   * 50 ms for the live conversation, where every added millisecond is felt as
   * hesitation. Reading a document is the other case: nobody notices a second
   * of lead, but they do notice a gap when the model briefly produces slower
   * than realtime, so the caller there asks for a real cushion.
   */
  constructor(private readonly leadSeconds = 0.05) {}

  /** Runs once everything scheduled has been played, after `finish()`. */
  onIdle: (() => void) | null = null;

  /**
   * The answer as a `MediaStream`, so the level meter can analyse it.
   *
   * WebRTC handed one over; here it is produced with a destination node the
   * playback is routed into as well. Null until the first chunk arrives, which is
   * also when there is nothing to show.
   */
  get stream(): MediaStream | null {
    return this.tap?.stream ?? null;
  }

  push(encoded: string): void {
    this.pushSamples(base64ToFloat(encoded));
  }

  /**
   * Creates the audio context while the click is still in scope.
   *
   * Only matters for the long read-aloud: its first chunk arrives seconds after
   * the button was pressed, and a context created then counts as unprovoked
   * under the autoplay policy. The live conversation primes it implicitly,
   * because its first delta follows the gesture within milliseconds.
   */
  prime(): void {
    this.context ??= new AudioContext({ sampleRate: PCM_RATE });
    void this.context.resume();
  }

  /**
   * The same as `push`, for a caller that already holds decoded samples.
   *
   * The explicit ArrayBuffer parameter matters, as it does in `base64ToFloat`:
   * `copyToChannel` refuses a Float32Array over an ArrayBufferLike.
   */
  pushSamples(samples: Float32Array<ArrayBuffer>): void {
    if (!samples.length) return;
    this.draining = false;

    const context = (this.context ??= new AudioContext({
      sampleRate: PCM_RATE,
    }));
    const buffer = context.createBuffer(1, samples.length, PCM_RATE);
    buffer.copyToChannel(samples, 0);

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    // Second output, only for the level meter — it makes no sound of its own.
    this.tap ??= context.createMediaStreamDestination();
    source.connect(this.tap);

    // A small lead: without it the first chunks are scheduled in the past and
    // are dropped, which sounds like the beginning of the sentence is missing.
    const startAt = Math.max(
      this.writeAt,
      context.currentTime + this.leadSeconds,
    );
    source.start(startAt);
    this.writeAt = startAt + buffer.duration;

    this.sources.add(source);
    source.onended = () => {
      this.sources.delete(source);
      if (this.draining && this.sources.size === 0) {
        this.draining = false;
        this.onIdle?.();
      }
    };
  }

  /**
   * No more chunks are coming: report back once the rest has been played.
   *
   * Needed because a streaming player cannot tell a finished take from a gap in
   * delivery — only the caller, who saw the response end, knows the difference.
   */
  finish(): void {
    this.draining = true;
    if (this.sources.size === 0) {
      this.draining = false;
      this.onIdle?.();
    }
  }

  /** Pause and resume. The context clock stops, so scheduled chunks wait too. */
  async suspend(): Promise<void> {
    await this.context?.suspend();
  }

  async resume(): Promise<void> {
    await this.context?.resume();
  }

  /** Barge-in: the advisor started talking, so the answer has to stop now. */
  stop(): void {
    this.draining = false;
    for (const source of this.sources) {
      try {
        source.stop();
      } catch {
        // Already ended.
      }
    }
    this.sources.clear();
    this.writeAt = 0;
  }

  close(): void {
    this.stop();
    this.tap?.disconnect();
    this.tap = null;
    void this.context?.close();
    this.context = null;
  }
}
